import type { ConnectorState, RecommendedConnector } from '../connector-promo-config-types.ts';

/**
 * Team type to recommended connector keys mapping.
 * Each team type maps to an ordered list of 3 connector keys.
 */
const TEAM_CONNECTOR_MAP: Record<string, string[]> = {
	SOFTWARE_DEVELOPMENT: ['github', 'drive', 'sharepoint'],
	PROJECT_MANAGEMENT: ['drive', 'sharepoint', 'github'],
	DATA_SCIENCE: ['drive', 'sharepoint', 'github'],
	MARKETING: ['drive', 'sharepoint', 'figma'],
	DESIGN: ['figma', 'drive', 'sharepoint'],
	CUSTOMER_SERVICE: ['sharepoint', 'drive', 'slack'],
	HUMAN_RESOURCES: ['sharepoint', 'drive', 'slack'],
	FINANCE: ['sharepoint', 'drive', 'slack'],
	LEGAL: ['sharepoint', 'drive', 'slack'],
	IT: ['sharepoint', 'github', 'drive'],
	SALES: ['drive', 'sharepoint', 'slack'],
	DEFAULT: ['drive', 'sharepoint', 'github'],
};

/**
 * Maps a team type and the current connector states to an ordered list of
 * recommended connectors that the user has NOT yet set up.
 *
 * Connectors with an enabled data source (hasEnabledDataSource === true) are
 * filtered out. They should not appear in the sidebar, empty state, or Nth
 * promo surfaces. The only place a set-up connector's name is referenced is
 * the post-connect promo, returned as connectedAppName.
 *
 * Connector state is derived from the admin_getAllDataSources AGG query.
 * A connector is "set up" when it has at least one AdminDataSource with
 * enabled === true.
 *
 * @param teamType - The uppercased team type (e.g., 'SOFTWARE_DEVELOPMENT')
 * @param connectorStates - A record mapping connector type keys to their state
 * @returns Unconnected recommendations and the first connected app's display name
 */
export function getRecommendedConnectors(
	teamType: string | undefined,
	connectorStates: Record<string, ConnectorState>,
): { recommendedConnectors: RecommendedConnector[]; connectedAppName: string | undefined } {
	const connectorKeys =
		teamType && TEAM_CONNECTOR_MAP[teamType]
			? TEAM_CONNECTOR_MAP[teamType]
			: TEAM_CONNECTOR_MAP.DEFAULT;

	const recommendedConnectors: RecommendedConnector[] = [];
	let connectedAppName: string | undefined;

	for (const key of connectorKeys) {
		const state = connectorStates[key];
		const isSetUp = state?.hasEnabledDataSource === true;

		if (isSetUp) {
			if (connectedAppName === undefined) {
				connectedAppName = state?.displayName ?? key;
			}
		} else {
			recommendedConnectors.push({
				key,
				displayName: state?.displayName ?? key,
			});
		}
	}

	return { recommendedConnectors, connectedAppName };
}
